import FilledButton from "@/components/button/filled-button";
import { PageContainer } from "@/components/container/page-container";
import React from "react";
import { isLikelyChunkError } from "@/utils/error/is-chunk-error";

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
}

export class GlobalErrorBoundary extends React.Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  state = { hasError: false };
  private reloadAttempted = false;

  static getDerivedStateFromError(error: Error) {
    console.info("Caught error in GlobalErrorBoundary:", error);
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.info("Caught error in GlobalErrorBoundary:", error);

    if (error instanceof Error) {
      const errorMessage = error.message;
      console.info("Error message:", errorMessage);
      if (isLikelyChunkError(errorMessage)) {
        console.warn("Detected failed dynamic import. Reloading page...");
        if (!this.reloadAttempted) {
          this.reloadAttempted = true;
          window.location.reload();
        }
        return;
      }
    }

    console.error("Error was not handled by GlobalErrorBoundary:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <PageContainer showLogo className="flex flex-col items-center justify-center gap-4">
          <h1>Oops, something went wrong</h1>
          <p>Reloading the page...</p>
          <p>If the page doesn't reload, please click the button below to reload the page.</p>
          <div className="w-full">
            <FilledButton onClick={() => window.location.reload()}>Reload</FilledButton>
          </div>
        </PageContainer>
      );
    }

    return this.props.children;
  }
}
